import { useState, useEffect, useReducer, useRef } from "react";
import { useRouter } from "next/router";
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import { SimpleBlockContent } from "../../SimpleBlockContent";
import { useCheckoutState, useCheckoutDispatch } from "@hooks/useCheckout";
import { useCurrencyState } from "@hooks/useCurrency";
import { useMobileDetect } from "@hooks/useMobileDetect";
import {
  getVariantOptions,
  getSize,
  getColor,
  getNavigation,
  getOption,
  nodeToId,
} from "../lib/product-utils";
import formatVariantPrice from "../lib/format-variant-price";
import { hasComingSoon } from "../lib/has-coming-soon";
import Form from "../../Form/Form";
import { SizeGuide } from "../";
import ProductFormSummary from "./ProductFormSummary";
import ProductFormSelection from "./ProductFormSelection";
import ProductFormDetails from "./ProductFormDetails";
import { ProductFormFooter } from "./ProductFormFooter";
import { trackEvent } from "@lib/track-event";
import { isBra } from "../lib/isBra";
import styles from "./styles/view.module.css";

const TRUE = "true";

// Get the currently active variant
function getVariant(variants, { size, option }) {
  let selectedVariant = variants[0];
  if (size && option) {
    selectedVariant = variants.find(
      (variant) => getOption(variant, option)?.toLowerCase() === size.toLowerCase()
    );
  }

  if (!selectedVariant) {
    selectedVariant = variants[0];
  }

  return selectedVariant;
}

// Get the initial variant
// We want to avoid showing the wait list on first view
function getFirstAvailableVariant(variants, { size, option }) {
  const firstAvailableVariant = variants.find((variant) => variant.availableForSale);

  if (size && option) {
    const variant = variants.find(
      (variant) =>
        variant.availableForSale && getOption(variant, option)?.toLowerCase() === size.toLowerCase()
    );

    // If the variant selected is sold out
    if (!variant && firstAvailableVariant) return firstAvailableVariant;
    // If everything is sold out
    if (!variant && !firstAvailableVariant) return variants[0];
    // If the variant selected is NOT sold out
    return variant;
  }

  if (!firstAvailableVariant) {
    return variants[0];
  }

  return firstAvailableVariant;
}

function reducer(state, action) {
  switch (action.type) {
    case "update":
      return { ...state, ...action.payload };
    default:
      throw new Error();
  }
}

const initState = (variant) => ({
  id: parseInt(nodeToId(variant.id ?? variant.shopifyVariantID), 10),
  quantity: 1,
  available: variant.availableForSale,
  image: variant.image,
  option: "size",
  size: getSize(variant),
  color: getColor(variant),
  hasZoom: false,
});

export default function ProductForm({
  productGroup,
  initProduct,
  settings,
  previewOnHover,
  openReviewsDrawer,
}) {
  const { loading } = useCheckoutState();
  const { addCartItem } = useCheckoutDispatch();
  const { currency } = useCurrencyState();
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [targetElement, setTargetElement] = useState(null);
  const router = useRouter();
  const currentDevice = useMobileDetect();
  const sizeGuideRef = useRef(null);

  useEffect(() => {
    setTargetElement(sizeGuideRef.current);
    return () => {
      clearAllBodyScrollLocks();
    };
  }, []);

  const isFrench = router.locale === "fr";

  let variants = null;
  let product = null;
  let finalSale = [];
  if (productGroup && productGroup.shopifyProducts) {
    product = productGroup.shopifyProducts.find(
      (product) => parseInt(nodeToId(product.productReferenceV2.id)[0], 10) === initProduct.store.id
    );
    variants = product.productReferenceV2.variants;
    finalSale = productGroup.shopifyProducts
      .filter(({ productReferenceV2 }) =>
        productReferenceV2.tags.find((tag) => tag === "final-sale")
      )
      .map(({ productReferenceV2 }) => productReferenceV2.id);
  } else {
    product = initProduct;
    variants = product.sourceData.variants.edges.map((variant) => variant.node);
  }
  const urlParams = new URLSearchParams(router.asPath);
  const { waitlist } = router.query;

  // `waitlist=true`
  // We want to show the waitlist if 'waitlist' query is true
  // This is needed because there are two different possible
  // behaviours depending on what page the user is coming from:
  // 1. Show waitlist for selected size
  // 2. Show first available size instead of waitlist
  const getInitialVariant = () => {
    if (waitlist === TRUE) {
      return getVariant(variants, {
        size: urlParams.get("size") ?? null,
        option: "size",
      });
    }

    return getFirstAvailableVariant(variants, {
      size: urlParams.get("size") ?? null,
      option: "size",
    });
  };

  const initialVariant = getInitialVariant();

  const isGiftCard = product?.productReferenceV2?.handle.toLowerCase() === "gift-card";

  const [state, dispatch] = useReducer(reducer, initialVariant, initState);

  const variant = getVariant(variants, state) ?? initialVariant;

  const { price, compareAtPrice, amount } = formatVariantPrice(variant, currency, 1);

  const update = (payload) => dispatch({ type: "update", payload });

  const options = getVariantOptions(variants, variant.selectedOptions);

  const navigation = getNavigation(productGroup, product, finalSale, isFrench);

  useEffect(() => {
    trackEvent("view_item", { product, productGroup, variant });
  }, [variant]);

  const handleOptionChange = (e) => {
    const size = e.value;
    const option = e.option;
    const available = variant.availableForSale;
    const updatedVariant = getVariant(variants, { size, option });
    update({ size, option, available, id: parseInt(nodeToId(e.id), 10) });
    trackEvent("customize_product", {
      category: "size",
      value: size,
      variant: updatedVariant,
    });
  };

  const handleNavigationChange = (e) => {
    const color = e.labelText;
    const available = variant.availableForSale;
    const newProduct = productGroup.shopifyProducts.find((p) => p.productReferenceV2.id === e.id);
    const updatedVariant = getVariant(newProduct.productReferenceV2.variants, {
      size: state.size,
      option: state.option,
    });
    previewOnHover(e.id);
    update({ color, available });
    trackEvent("customize_product", { category: "color", value: color, variant: updatedVariant });
  };

  const addToCart = () => {
    addCartItem(variant, Number(state.quantity), productGroup);
  };

  function closeSizeGuide() {
    setShowSizeGuide(false);
    if (currentDevice.isMobile()) {
      enableBodyScroll(targetElement);
    }
  }

  function openSizeGuide() {
    setShowSizeGuide(true);
    if (currentDevice.isMobile()) {
      disableBodyScroll(targetElement);
    }
  }

  console.log('RARES', price, compareAtPrice, amount);

  return (
    <>
      {productGroup.sizeGuide && (
        <SizeGuide
          variant={variant}
          data={productGroup.sizeGuide}
          isBra={isBra(productGroup.slug)}
          showSizeGuide={showSizeGuide}
          closeSizeGuide={closeSizeGuide}
          selectedSize={state.size}
          sizes={options.get("Size")}
          handleOptionChange={handleOptionChange}
          addToCart={addToCart}
          productTitle={productGroup.title.replace(/(Wo)?[Mm]en['’]?s /g, "")}
          comingSoon={hasComingSoon(productGroup, initProduct.id)}
          waitlistMessage={product.waitlistMessage}
          ref={sizeGuideRef}
        />
      )}
      <div className={styles.wrapper}>
        <Form onSubmit={addToCart} noValidate>
          <div className={styles.formBody}>
            <ProductFormSummary
              product={product.productReferenceV2}
              title={productGroup.title.replace(/(Wo)?[Mm]en['’]?s /g, "")}
              price={price}
              amount={amount}
              currency={currency}
              compareAtPrice={compareAtPrice}
              finalSale={finalSale.includes(navigation.find((prod) => prod.selected)?.id)}
              description={<SimpleBlockContent blocks={productGroup.description} />}
              openReviewsDrawer={openReviewsDrawer}
              styledWith={product.styledWith}
              bundleLinks={productGroup.bundleLinks}
            />
            <ProductFormSelection
              product={product}
              navigation={navigation}
              options={options}
              handleNavigationChange={handleNavigationChange}
              handleOptionChange={handleOptionChange}
              openSizeGuide={productGroup.sizeGuide ? openSizeGuide : null}
            />
            <ProductFormDetails
              product={productGroup}
              settings={settings}
              isGiftCard={isGiftCard}
            />
          </div>
          <ProductFormFooter
            variant={variant}
            loading={loading}
            addToCart={addToCart}
            productTitle={productGroup.title.replace(/(Wo)?[Mm]en['’]?s /g, "")}
            comingSoon={hasComingSoon(productGroup, initProduct.id)}
            waitlistMessage={product.waitlistMessage}
          />
        </Form>
      </div>
    </>
  );
}
